// src/components/dashboard/shell/OperationsShell.js

import { useNavigate } from "react-router-dom";
import { BRAND } from "../../../config/brand";
import NotificationBell from "../NotificationBell";

/**
 * Shared chrome for the expert and customer consoles.
 *
 * Each dashboard used to carry its own copy of this header in a different brand
 * colour — purple for experts, blue for customers — while the incident
 * workspace used a third palette, so the three surfaces read as three separate
 * products. One shell, one accent, matching the workspace.
 *
 * The shell also owns identity and tabs. Both were previously rendered as a
 * separate full-width "Welcome, <name>" card that pushed the actual work below
 * the fold without telling anyone anything they did not already know.
 */

function initialsOf(nameOrEmail) {
  if (!nameOrEmail) return "?";
  return nameOrEmail
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function NavButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {children}
    </button>
  );
}

export default function OperationsShell({
  user,
  roleLabel,
  historyPath,
  onLogout,
  tabs = [],
  activeTab,
  onTabChange,
  children,
}) {
  const navigate = useNavigate();
  const displayName = user?.displayName || user?.email || "Operator";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-14 max-w-console items-center gap-3 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex-shrink-0 text-base font-semibold tracking-tight text-slate-900"
          >
            {BRAND.name}
          </button>

          <span className="hidden text-xs text-slate-400 sm:inline">
            Operations console
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            <NotificationBell currentUser={user} />
            {historyPath && (
              <NavButton onClick={() => navigate(historyPath)}>
                History
              </NavButton>
            )}
            <NavButton onClick={() => navigate("/profile")}>Profile</NavButton>
            <button
              type="button"
              onClick={onLogout}
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Log out
            </button>
          </div>
        </div>

        <div className="mx-auto flex max-w-console flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
              {initialsOf(user?.displayName || user?.email)}
            </span>
            <span className="text-sm font-medium text-slate-800">
              {displayName}
            </span>
            {roleLabel && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
                {roleLabel}
              </span>
            )}
          </div>

          {tabs.length > 0 && (
            <nav className="-mb-px flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const selected = tab.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => onTabChange(tab.id)}
                    aria-current={selected ? "page" : undefined}
                    className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      selected
                        ? "border-blue-600 text-blue-700"
                        : "border-transparent text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {tab.label}
                    {tab.badge > 0 && (
                      <span
                        className={`ml-2 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${
                          selected
                            ? "bg-blue-50 text-blue-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-console space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </main>

      <footer className="mx-auto max-w-console px-4 pb-8 sm:px-6 lg:px-8">
        <p className="text-xs text-slate-400">
          {BRAND.productName} ·{" "}
          <button
            type="button"
            onClick={() =>
              window.open(
                "https://docs.google.com/forms/d/e/1FAIpQLSeWg_Xujw9QtmciVt7g48cdWorEffVAbVNjC63ACrIWj5G4fA/viewform?usp=dialog",
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="underline hover:text-slate-600"
          >
            Send feedback
          </button>
        </p>
      </footer>
    </div>
  );
}
